/**
 * Poker Grudge wallet context — Grudge Studio fleet aligned.
 * Identity (Grudge ID JWT) before wallet; third-party Solana opt-in only.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  captureSsoTokenFromUrl,
  clearGrudgeToken,
  getGrudgeToken,
  isLoggedIn,
  loginWithGrudgeId,
} from "@/lib/grudge-auth";
import {
  connectSolanaWallet,
  disconnectSolanaWallet,
  detectSolanaWallets,
  ensureSigningWallet,
  signMessageBase58,
  type ConnectedSolanaWallet,
  type SolanaWalletId,
} from "@/lib/solana-wallets";
import { roundPlayGbux } from "@shared/gbux-math";
import {
  fetchWalletOverview,
  linkWalletChallenge,
  linkWalletConfirm,
} from "@/lib/grudge-wallet-api";
import { fetchUnifiedWalletSnapshot } from "@/lib/crossmint-client";
import {
  fetchOnChainGbux,
  fetchOnChainSol,
} from "@/lib/gbux-transfer";
import { GBUX } from "@shared/fleet";
import {
  clearWalletRegistry,
  listRegisteredWallets,
  registerWallet,
  removeRegisteredWallet,
  setActiveWalletAddress,
  updateWalletBalanceCache,
  type RegisteredWallet,
} from "@/lib/wallet-registry";

export interface WalletState {
  publicKey: string | null;
  connected: boolean;
  walletType: string | null;
  walletId: SolanaWalletId | null;
  gbuxBalance: number;
  solBalance: number;
  /** Grudge ID session present */
  isAuthenticated: boolean;
  /** Account GBUX from Railway overview when logged in */
  accountGbux: number;
  /** Edge D1 — real play GBUX for this dApp (tables debit on join); not fake chips */
  playGbux: number;
  /** Crossmint custodial address from fleet overview */
  custodialWallet: string | null;
  /** Linked external wallets from fleet overview */
  linkedWallets: Array<{ address: string; provider: string }>;
  /**
   * Multi-wallet registry (this browser) — public addresses + cached balances only.
   * Never secrets. @see wallet-registry.ts
   */
  multiWallets: RegisteredWallet[];
  /** On-chain GBUX for connected external key */
  chainGbux: number;
  /** On-chain SOL for connected external key */
  chainSol: number;
  /** Crossmint custodial on-chain GBUX (if different address) */
  custodialChainGbux: number;
  custodialChainSol: number;
  /** Ready to sit: wallet address + can call join API */
  canSit: boolean;
  /** Balance source tags from edge unified API */
  balanceSources: string[];
}

/** Global wallet drawer tabs — transfer funds play; swap is fleet bag */
export type WalletPanelTab =
  | "overview"
  | "transfer"
  | "deposit" // alias → transfer (legacy openWalletPanel("deposit"))
  | "swap"
  | "connect";

interface WalletContextType {
  wallet: WalletState;
  updateWallet: (walletData: Partial<WalletState>) => void;
  clearWallet: () => void;
  refreshFromStorage: () => void;
  refreshBalances: () => Promise<void>;
  connect: (
    walletId?: SolanaWalletId,
    opts?: { phantomMode?: "injected" | "google" | "apple" },
  ) => Promise<{ publicKey: string } | null>;
  disconnect: () => Promise<void>;
  /**
   * Switch active address among registered multi-wallets (display + play scopes).
   * Does not re-prompt OAuth unless ensureSigner is needed for signing.
   */
  selectWallet: (address: string) => Promise<void>;
  /** Remove one address from local multi-wallet registry */
  forgetWallet: (address: string) => void;
  /** Refresh multi-wallet balance cache via edge batch (best effort) */
  refreshMultiWalletBalances: () => Promise<void>;
  linkToGrudgeAccount: () => Promise<void>;
  loginGrudgeId: (returnPath?: string) => void;
  detectedWallets: ReturnType<typeof detectSolanaWallets>;
  rescanWallets: () => void;
  /** Active injected wallet for signing transfers (null if only custodial address) */
  getConnectedWallet: () => ConnectedSolanaWallet | null;
  /**
   * Ensure Phantom/Solflare provider is bound for signing (after refresh).
   * silentOnly true (default) = no popup; false = user-initiated reconnect/transfer.
   */
  ensureSigner: (opts?: {
    silentOnly?: boolean;
  }) => Promise<ConnectedSolanaWallet | null>;
  /** True when activeProvider can sign for wallet.publicKey */
  canSign: boolean;
  /** Global wallet / account pop-out */
  walletPanelOpen: boolean;
  walletPanelTab: WalletPanelTab;
  openWalletPanel: (tab?: WalletPanelTab) => void;
  closeWalletPanel: () => void;
}

const WalletContext = createContext<WalletContextType | null>(null);

export function useWalletState() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWalletState must be used within WalletProvider");
  }
  return context;
}

const initialWalletState: WalletState = {
  publicKey: null,
  connected: false,
  walletType: null,
  walletId: null,
  gbuxBalance: 0,
  solBalance: 0,
  isAuthenticated: false,
  accountGbux: 0,
  playGbux: 0,
  custodialWallet: null,
  linkedWallets: [],
  multiWallets: [],
  chainGbux: 0,
  chainSol: 0,
  custodialChainGbux: 0,
  custodialChainSol: 0,
  canSit: false,
  balanceSources: [],
};

let activeProvider: ConnectedSolanaWallet | null = null;

export function WalletProvider({ children }: { children: ReactNode }) {
  const [wallet, setWallet] = useState<WalletState>(initialWalletState);
  const [detectedWallets, setDetectedWallets] = useState(() =>
    typeof window !== "undefined" ? detectSolanaWallets() : [],
  );
  const [walletPanelOpen, setWalletPanelOpen] = useState(false);
  const [walletPanelTab, setWalletPanelTab] =
    useState<WalletPanelTab>("overview");
  const [canSign, setCanSign] = useState(false);

  const rescanWallets = useCallback(() => {
    setDetectedWallets(detectSolanaWallets());
  }, []);

  const syncCanSign = useCallback(() => {
    const ok = Boolean(
      activeProvider &&
        activeProvider.publicKey &&
        (!wallet.publicKey ||
          activeProvider.publicKey === wallet.publicKey),
    );
    setCanSign(ok);
    return ok;
  }, [wallet.publicKey]);

  const updateWallet = useCallback((walletData: Partial<WalletState>) => {
    setWallet((prev) => {
      const next = { ...prev, ...walletData };
      try {
        localStorage.setItem(
          "poker_grudge_wallet",
          JSON.stringify({
            publicKey: next.publicKey,
            walletType: next.walletType,
            walletId: next.walletId,
            connected: next.connected,
          }),
        );
        // Legacy key some pages still read
        if (next.publicKey && next.connected) {
          localStorage.setItem(
            "ohio_user",
            JSON.stringify({
              walletAddress: next.publicKey,
              walletType: next.walletType,
              gbuxBalance: String(next.gbuxBalance),
              solBalance: String(next.solBalance),
              isAuthenticated: next.isAuthenticated,
            }),
          );
        }
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const refreshBalances = useCallback(async () => {
    const token = getGrudgeToken();
    let accountGbux = 0;
    let custodialWallet: string | null = null;
    let linkedWallets: Array<{ address: string; provider: string }> = [];

    if (token) {
      try {
        const overview = await fetchWalletOverview();
        if (overview) {
          accountGbux = parseFloat(String(overview.gbuxBalance ?? 0)) || 0;
          custodialWallet =
            (overview.custodialWallet as string) ||
            (overview.primaryWallet as string) ||
            null;
          if (Array.isArray(overview.linkedWallets)) {
            linkedWallets = overview.linkedWallets
              .map((w) => ({
                address: String(w.address || ""),
                provider: String(w.provider || "linked"),
              }))
              .filter((w) => w.address.length >= 32);
          }
        }
      } catch {
        /* fleet offline */
      }
    }

    // Resolve public key for scopes (prefer active signer, then storage, then custodial)
    let publicKey: string | null = activeProvider?.publicKey || null;
    if (!publicKey || publicKey.length < 32) {
      // read latest from state via functional pattern below if needed
      try {
        const raw = localStorage.getItem("poker_grudge_wallet");
        if (raw) {
          const s = JSON.parse(raw) as { publicKey?: string };
          if (s.publicKey && s.publicKey.length >= 32) publicKey = s.publicKey;
        }
      } catch {
        /* ignore */
      }
    }
    if (!publicKey || publicKey.length < 32) {
      publicKey = custodialWallet || linkedWallets[0]?.address || null;
    }

    if (!publicKey || publicKey.length < 32) {
      setWallet((prev) => ({
        ...prev,
        isAuthenticated: Boolean(token),
        accountGbux: roundPlayGbux(accountGbux),
        custodialWallet,
        linkedWallets,
        canSit: false,
      }));
      syncCanSign();
      return;
    }

    // Edge scopes first (Helius on Worker). Skip public mainnet RPC (browser 403).
    let playGbux = 0;
    let chainGbux = 0;
    let chainSol = 0;
    let sources: string[] = [];
    let snapAccount: number | null = null;
    let snapCustodial: string | null = null;
    let snapCustodialGbux = 0;
    let snapCustodialSol = 0;

    try {
      const snap = await fetchUnifiedWalletSnapshot(publicKey).catch(() => null);
      // Optional private RPC only (VITE_SOLANA_RPC) — never api.mainnet-beta.solana.com
      const [rpcGbux, rpcSol] = await Promise.all([
        fetchOnChainGbux(publicKey).catch(() => 0),
        fetchOnChainSol(publicKey).catch(() => 0),
      ]);
      if (snap) {
        playGbux = roundPlayGbux(snap.playGbux);
        const edgeGbux = Number(snap.external?.gbuxOnChain ?? 0) || 0;
        const edgeSol = Number(snap.external?.sol ?? 0) || 0;
        chainGbux = Math.max(edgeGbux, rpcGbux);
        chainSol = Math.max(edgeSol, rpcSol);
        sources = [
          ...(snap.sources || ["unified"]),
          rpcGbux > 0 || rpcSol > 0 ? "rpc" : "",
        ].filter(Boolean);
        snapAccount =
          snap.accountGbux != null ? roundPlayGbux(snap.accountGbux) : null;
        snapCustodial = snap.custodial.address;
        snapCustodialGbux = Number(snap.custodial.gbuxOnChain ?? 0) || 0;
        snapCustodialSol = Number(snap.custodial.sol ?? 0) || 0;
      } else {
        chainGbux = rpcGbux;
        chainSol = rpcSol;
        sources = ["rpc"];
      }
    } catch {
      sources = ["error"];
    }

    setWallet((prev) => ({
      ...prev,
      publicKey,
      connected: true,
      walletType:
        prev.walletType ||
        activeProvider?.name ||
        (custodialWallet === publicKey ? "Custodial" : prev.walletType),
      walletId: prev.walletId || activeProvider?.id || prev.walletId,
      isAuthenticated: Boolean(token),
      playGbux,
      gbuxBalance: playGbux,
      chainGbux,
      chainSol,
      solBalance: chainSol,
      accountGbux:
        snapAccount != null ? snapAccount : roundPlayGbux(accountGbux),
      custodialWallet: snapCustodial || custodialWallet,
      custodialChainGbux: snapCustodialGbux,
      custodialChainSol: snapCustodialSol,
      linkedWallets,
      multiWallets: listRegisteredWallets(),
      balanceSources: sources,
      canSit: true,
    }));
    if (publicKey) {
      updateWalletBalanceCache(publicKey, {
        chainGbux,
        chainSol,
        playGbux,
      });
    }
    syncCanSign();
  }, [syncCanSign]);

  const refreshMultiWalletBalances = useCallback(async () => {
    const list = listRegisteredWallets();
    if (!list.length) {
      setWallet((prev) => ({ ...prev, multiWallets: [] }));
      return;
    }
    try {
      const addresses = list.map((w) => w.address).slice(0, 8);
      const res = await fetch("/api/wallet/scopes-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallets: addresses }),
      });
      if (res.ok) {
        const data = (await res.json()) as {
          results?: Array<{
            address: string;
            sol?: number;
            gbuxOnChain?: number;
            playGbux?: number;
          }>;
        };
        for (const r of data.results || []) {
          if (r.address) {
            updateWalletBalanceCache(r.address, {
              chainSol: Number(r.sol) || 0,
              chainGbux: Number(r.gbuxOnChain) || 0,
              playGbux: Number(r.playGbux) || 0,
            });
          }
        }
      } else {
        // Fallback: RPC for first 3 only (avoid rate limits)
        await Promise.all(
          list.slice(0, 3).map(async (w) => {
            const [g, s] = await Promise.all([
              fetchOnChainGbux(w.address).catch(() => 0),
              fetchOnChainSol(w.address).catch(() => 0),
            ]);
            updateWalletBalanceCache(w.address, {
              chainGbux: g,
              chainSol: s,
            });
          }),
        );
      }
    } catch {
      /* offline */
    }
    setWallet((prev) => ({
      ...prev,
      multiWallets: listRegisteredWallets(),
    }));
  }, []);

  const refreshFromStorage = useCallback(() => {
    captureSsoTokenFromUrl();
    const authed = isLoggedIn();
    try {
      const multi = listRegisteredWallets();
      const raw = localStorage.getItem("poker_grudge_wallet");
      if (raw) {
        const s = JSON.parse(raw);
        if (s.publicKey) {
          setWallet((prev) => ({
            ...prev,
            publicKey: s.publicKey,
            connected: Boolean(s.connected),
            walletType: s.walletType || null,
            walletId: s.walletId || null,
            isAuthenticated: authed,
            multiWallets: multi,
          }));
          return;
        }
      }
      const legacy = localStorage.getItem("ohio_user");
      if (legacy) {
        const u = JSON.parse(legacy);
        if (u.walletAddress) {
          setWallet((prev) => ({
            ...prev,
            publicKey: u.walletAddress,
            connected: true,
            walletType: u.walletType || "Phantom",
            isAuthenticated: authed,
            gbuxBalance: parseFloat(u.gbuxBalance || "0") || 0,
          }));
          return;
        }
      }
    } catch {
      /* ignore */
    }
    setWallet((prev) => ({
      ...prev,
      isAuthenticated: authed,
    }));
  }, []);

  useEffect(() => {
    refreshFromStorage();
  }, [refreshFromStorage]);

  // Resume Grudge ID + Phantom Connect on EVERY route (not only /connect).
  useEffect(() => {
    if (typeof window === "undefined") return;
    void (async () => {
      try {
        const { resumePhantomAsConnected } = await import(
          "@/lib/phantom-browser"
        );
        const c = await resumePhantomAsConnected();
        if (c) {
          activeProvider = c;
          setCanSign(true);
          registerWallet({
            address: c.publicKey,
            walletId: "phantom",
            label: c.name,
            phantomMode: "google",
          });
          setActiveWalletAddress(c.publicKey);
          updateWallet({
            publicKey: c.publicKey,
            connected: true,
            walletType: c.name,
            walletId: "phantom",
            isAuthenticated: isLoggedIn(),
            multiWallets: listRegisteredWallets(),
          });
        } else {
          const silent = await ensureSigningWallet({
            walletId: "phantom",
            publicKey: null,
            silentOnly: true,
          });
          if (silent) {
            activeProvider = silent;
            setCanSign(true);
            updateWallet({
              publicKey: silent.publicKey,
              connected: true,
              walletType: silent.name,
              walletId: silent.id,
              isAuthenticated: isLoggedIn(),
            });
          }
        }
        await refreshBalances();
      } catch {
        /* first visit — user taps Connect once */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount
  }, []);

  // Balance refresh on login / wallet change + tab focus (dapp best practice)
  useEffect(() => {
    void refreshBalances();
  }, [wallet.publicKey, wallet.isAuthenticated]);

  useEffect(() => {
    if (!wallet.publicKey && !wallet.isAuthenticated) return;
    const onVis = () => {
      if (document.visibilityState === "visible") void refreshBalances();
    };
    const onFocus = () => void refreshBalances();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    // Periodic chain + play recheck while app open (5 min)
    const tick = window.setInterval(() => void refreshBalances(), 300_000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(tick);
    };
  }, [wallet.publicKey, wallet.isAuthenticated, refreshBalances]);

  const connect = useCallback(
    async (
      walletId: SolanaWalletId = "phantom",
      opts?: { phantomMode?: "injected" | "google" | "apple" },
    ) => {
      rescanWallets();
      // Phantom: official @phantom/browser-sdk (App ID + Portal domain)
      if (walletId === "phantom") {
        try {
          const { connectWithPhantomSdk } = await import("@/lib/phantom-browser");
          const mode = opts?.phantomMode || "injected";
          const connected = await connectWithPhantomSdk(mode);
          activeProvider = connected;
          setCanSign(true);
          const label =
            mode === "injected"
              ? "Phantom"
              : mode === "google"
                ? "Phantom (Google)"
                : "Phantom (Apple)";
          registerWallet({
            address: connected.publicKey,
            walletId: "phantom",
            label,
            phantomMode: mode,
          });
          setActiveWalletAddress(connected.publicKey);
          updateWallet({
            publicKey: connected.publicKey,
            connected: true,
            walletType: label,
            walletId: "phantom",
            isAuthenticated: isLoggedIn(),
            multiWallets: listRegisteredWallets(),
          });
          await refreshBalances();
          void refreshMultiWalletBalances();
          return { publicKey: connected.publicKey };
        } catch (e) {
          if (e instanceof Error && e.message === "REDIRECT_IN_PROGRESS") {
            // OAuth redirect — page will return to /connect
            throw e;
          }
          console.warn(
            "[wallet] Phantom Browser SDK connect failed, trying inject",
            e,
          );
        }
      }
      const available = detectSolanaWallets().filter((w) => w.available);
      let id = walletId;
      if (!available.find((w) => w.id === id)) {
        id = available[0]?.id || walletId;
      }
      const connected = await connectSolanaWallet(id, {
        onlyIfTrusted: false,
      });
      activeProvider = connected;
      setCanSign(true);
      registerWallet({
        address: connected.publicKey,
        walletId: connected.id,
        label: connected.name,
      });
      setActiveWalletAddress(connected.publicKey);
      updateWallet({
        publicKey: connected.publicKey,
        connected: true,
        walletType: connected.name,
        walletId: connected.id,
        isAuthenticated: isLoggedIn(),
        multiWallets: listRegisteredWallets(),
      });
      await refreshBalances();
      void refreshMultiWalletBalances();
      return { publicKey: connected.publicKey };
    },
    [updateWallet, refreshBalances, rescanWallets, refreshMultiWalletBalances],
  );

  const selectWallet = useCallback(
    async (address: string) => {
      if (!address || address.length < 32) return;
      const reg = listRegisteredWallets().find((w) => w.address === address);
      setActiveWalletAddress(address);
      // Drop signing session if switching away from current signer key
      if (activeProvider && activeProvider.publicKey !== address) {
        activeProvider = null;
        setCanSign(false);
      }
      updateWallet({
        publicKey: address,
        connected: true,
        walletType: reg?.label || "Wallet",
        walletId: reg?.walletId || "generic",
        multiWallets: listRegisteredWallets(),
      });
      await refreshBalances();
    },
    [updateWallet, refreshBalances],
  );

  /**
   * Re-bind signing session.
   * - silentOnly (default true): onlyIfTrusted / already-connected — no popup
   * - silentOnly false: user clicked Reconnect/Transfer — may open wallet UI
   */
  const ensureSigner = useCallback(
    async (opts?: { silentOnly?: boolean }) => {
      const silentOnly = opts?.silentOnly !== false;
      if (
        activeProvider &&
        (!wallet.publicKey || activeProvider.publicKey === wallet.publicKey)
      ) {
        setCanSign(true);
        return activeProvider;
      }
      // Injected rebind
      const bound = await ensureSigningWallet({
        walletId: wallet.walletId || "phantom",
        publicKey: wallet.publicKey,
        silentOnly,
      });
      if (bound) {
        activeProvider = bound;
        setCanSign(true);
        updateWallet({
          publicKey: bound.publicKey,
          connected: true,
          walletType: bound.name,
          walletId: bound.id,
        });
        await refreshBalances();
        return bound;
      }
      // Full prompt path (user-initiated only)
      if (!silentOnly && (!wallet.walletId || wallet.walletId === "phantom")) {
        try {
          const { connectWithPhantomSdk } = await import(
            "@/lib/phantom-browser"
          );
          const c = await connectWithPhantomSdk("injected");
          if (!wallet.publicKey || c.publicKey === wallet.publicKey) {
            activeProvider = c;
            setCanSign(true);
            updateWallet({
              publicKey: c.publicKey,
              connected: true,
              walletType: "Phantom",
              walletId: "phantom",
            });
            await refreshBalances();
            return c;
          }
        } catch {
          /* fall through */
        }
        // Last resort: other inject wallets with full connect
        const full = await ensureSigningWallet({
          walletId: wallet.walletId || "phantom",
          publicKey: wallet.publicKey,
          silentOnly: false,
        });
        if (full) {
          activeProvider = full;
          setCanSign(true);
          updateWallet({
            publicKey: full.publicKey,
            connected: true,
            walletType: full.name,
            walletId: full.id,
          });
          await refreshBalances();
          return full;
        }
      }
      setCanSign(false);
      return null;
    },
    [wallet.publicKey, wallet.walletId, updateWallet, refreshBalances],
  );

  const disconnect = useCallback(async () => {
    try {
      const { disconnectPhantomSdk } = await import("@/lib/phantom-browser");
      await disconnectPhantomSdk();
    } catch {
      /* ignore */
    }
    try {
      const { clearPlaySession } = await import("@/lib/play-session");
      clearPlaySession();
    } catch {
      /* ignore */
    }
    await disconnectSolanaWallet(activeProvider);
    activeProvider = null;
    setCanSign(false);
    // Keep multi-wallet address history for re-select; clear active session keys
    clearWalletRegistry({ keepHistory: true });
    setWallet({
      ...initialWalletState,
      multiWallets: listRegisteredWallets(),
    });
    try {
      localStorage.removeItem("poker_grudge_wallet");
      localStorage.removeItem("ohio_user");
    } catch {
      /* ignore */
    }
  }, []);

  const forgetWallet = useCallback(
    (address: string) => {
      removeRegisteredWallet(address);
      setWallet((prev) => {
        if (prev.publicKey === address) {
          const next = listRegisteredWallets()[0];
          if (next) {
            setActiveWalletAddress(next.address);
            activeProvider = null;
            setCanSign(false);
            return {
              ...prev,
              publicKey: next.address,
              walletType: next.label,
              walletId: next.walletId,
              multiWallets: listRegisteredWallets(),
            };
          }
          void disconnect();
          return { ...prev, multiWallets: [] };
        }
        return { ...prev, multiWallets: listRegisteredWallets() };
      });
    },
    [disconnect],
  );

  const clearWallet = useCallback(() => {
    void disconnect();
  }, [disconnect]);

  /** Link connected Solana key to Grudge account (challenge-sign). */
  const linkToGrudgeAccount = useCallback(async () => {
    if (!isLoggedIn()) {
      throw new Error("Sign in with Grudge ID first");
    }
    if (!wallet.publicKey || !activeProvider) {
      throw new Error("Connect a Solana wallet first");
    }
    const { message, walletAddress } = await linkWalletChallenge(
      wallet.publicKey,
    );
    const signature = await signMessageBase58(activeProvider, message);
    await linkWalletConfirm({
      walletAddress,
      message,
      signature,
      provider: (wallet.walletId as string) || "other",
      label: wallet.walletType || undefined,
    });
    await refreshBalances();
  }, [wallet, refreshBalances]);

  const loginGrudgeId = useCallback((returnPath = "/lobby") => {
    loginWithGrudgeId(returnPath);
  }, []);

  const getConnectedWallet = useCallback(() => {
    if (
      activeProvider &&
      (!wallet.publicKey || activeProvider.publicKey === wallet.publicKey)
    ) {
      return activeProvider;
    }
    return null;
  }, [wallet.publicKey, wallet.walletId, wallet.connected, canSign]);

  const openWalletPanel = useCallback(
    (tab: WalletPanelTab = "overview") => {
      const normalized: WalletPanelTab =
        tab === "deposit" ? "transfer" : tab;
      setWalletPanelTab(normalized);
      setWalletPanelOpen(true);
      rescanWallets();
      // Rehydrate balances + silent signer bind (no wallet popup)
      void (async () => {
        await refreshBalances();
        if (wallet.publicKey && !activeProvider) {
          try {
            const c = await ensureSigningWallet({
              walletId: wallet.walletId,
              publicKey: wallet.publicKey,
              silentOnly: true,
            });
            if (c) {
              activeProvider = c;
              setCanSign(true);
            } else {
              setCanSign(false);
            }
          } catch {
            setCanSign(false);
          }
        } else {
          syncCanSign();
        }
      })();
    },
    [
      rescanWallets,
      refreshBalances,
      wallet.publicKey,
      wallet.walletId,
      syncCanSign,
    ],
  );

  const closeWalletPanel = useCallback(() => {
    setWalletPanelOpen(false);
  }, []);

  return (
    <WalletContext.Provider
      value={{
        wallet,
        updateWallet,
        clearWallet,
        refreshFromStorage,
        refreshBalances,
        connect,
        disconnect,
        selectWallet,
        forgetWallet,
        refreshMultiWalletBalances,
        linkToGrudgeAccount,
        loginGrudgeId,
        detectedWallets,
        rescanWallets,
        getConnectedWallet,
        ensureSigner,
        canSign,
        walletPanelOpen,
        walletPanelTab,
        openWalletPanel,
        closeWalletPanel,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const ctx = useWalletState();
  const play = ctx.wallet.playGbux || ctx.wallet.gbuxBalance;
  return {
    ...ctx,
    publicKey: ctx.wallet.publicKey
      ? { toString: () => ctx.wallet.publicKey! }
      : null,
    connected: ctx.wallet.connected || ctx.wallet.canSit,
    connecting: false,
    connect: (id?: SolanaWalletId) => ctx.connect(id),
    disconnect: ctx.disconnect,
    /** Play ledger only — consistent with tables/slots */
    gbuxBalance: play,
    accountGbux: ctx.wallet.accountGbux,
    playGbux: play,
    chainGbux: ctx.wallet.chainGbux,
    custodialWallet: ctx.wallet.custodialWallet,
    isAuthenticated: ctx.wallet.isAuthenticated,
    canSit: ctx.wallet.canSit,
    mint: GBUX.mint,
    refreshBalances: ctx.refreshBalances,
    loginGrudgeId: ctx.loginGrudgeId,
    openWalletPanel: ctx.openWalletPanel,
    closeWalletPanel: ctx.closeWalletPanel,
  };
}

export async function connectWallet(): Promise<{ publicKey: string } | null> {
  // Legacy helper — prefer useWallet().connect
  try {
    const c = await connectSolanaWallet("phantom");
    activeProvider = c;
    return { publicKey: c.publicKey };
  } catch {
    try {
      const c = await connectSolanaWallet("solflare");
      activeProvider = c;
      return { publicKey: c.publicKey };
    } catch {
      return null;
    }
  }
}

export { clearGrudgeToken };
